import { createAsyncThunk } from '@reduxjs/toolkit';
import { loadUsersAndPersonalResourcesByUserIds } from 'store/projectUsers/thunks/loadUsersAndPersonalResourcesByUserIds';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { getProjectActivityFeed } from '../services/getProjectActivityFeed.service';

/** Rows of the dashboard's feed, and of each page the dialog loads more of. */
export const PROJECT_ACTIVITY_FEED_PAGE_SIZE = 20;

/**
 * The most API pages one load requests while none of them has a row to show.
 * Each costs the API at most 1000 examined rows, so a project whose newest
 * thousands of rows are all left out still stops here, keeping the cursor.
 */
export const PROJECT_ACTIVITY_FEED_MAX_PAGES_PER_LOAD = 5;

// Someone opening the project is the most common row of the log and says
// little, so the feed does not show it. The API leaves it out as well; this
// keeps it out when the API is one that does not yet.
export const isShownInProjectFeed = (item) => item.action !== 'VIEW_PROJECT';

/**
 * Loads a page of the project's activity feed: the newest rows, or with
 * `before` (the previous page's `nextBefore`) the next older page. Users the
 * store does not know yet are loaded alongside, for their profile pictures.
 * Resolves `{ items, nextBefore }` with only the rows the feed shows.
 *
 * An API page can hold nothing to show while older rows follow (the rows of
 * opening the project filled it), so this follows `nextBefore` until a page
 * has a row to show, the feed ends, or PROJECT_ACTIVITY_FEED_MAX_PAGES_PER_LOAD
 * pages have been asked for. Without that the dashboard, which loads only the
 * first page, stayed blank until the dialog's "Lataa lisää" loaded older ones.
 */
export const fetchProjectActivityFeed = createAsyncThunk(
  'activityLog/fetchProjectFeed',
  async ({ projectId, before }, { dispatch, getState }) => {
    const state = getState();
    const activeEmployerInfo = selectUserActiveEmployerInfo(state);

    let items = [];
    let cursor = before;
    for (
      let pages = 0;
      pages < PROJECT_ACTIVITY_FEED_MAX_PAGES_PER_LOAD;
      pages += 1
    ) {
      const data = await getProjectActivityFeed({
        projectId,
        limit: PROJECT_ACTIVITY_FEED_PAGE_SIZE,
        before: cursor,
        activeEmployerInfo,
      });
      items = items.concat((data?.items || []).filter(isShownInProjectFeed));
      cursor = data?.nextBefore || null;
      if (items.length > 0 || !cursor) break;
    }

    const knownUsers = state.projectUsers?.users?.entities || {};
    const missingUserIds = [
      ...new Set(items.map((item) => item.userId).filter(Boolean)),
    ].filter((userId) => !knownUsers[userId]);
    if (missingUserIds.length > 0) {
      dispatch(loadUsersAndPersonalResourcesByUserIds(missingUserIds));
    }

    return { items, nextBefore: cursor };
  }
);
